import { useState } from "react";
import CrudPage from "@/components/app/CrudPage";
import api, { errMsg } from "@/lib/api";
import { CalendarClock, X, KeyRound, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function SuppliersPage() {
  const [showBulk, setShowBulk] = useState(false);
  const [terms, setTerms] = useState("30 GÜN");
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [portalResult, setPortalResult] = useState(null);
  const [creatingPortal, setCreatingPortal] = useState(false);

  const createPortalAccounts = async () => {
    if (!window.confirm("Hesabı olmayan TÜM tedarikçilere portal hesabı açılacak (cariadi@keskin.com / 123456). Devam edilsin mi?")) return;
    setCreatingPortal(true);
    try {
      const { data } = await api.post("/suppliers/create-portal-accounts");
      setPortalResult(data);
      toast.success(`${data.created_count} yeni hesap oluşturuldu, ${data.existing_count} tedarikçinin hesabı zaten vardı`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCreatingPortal(false); }
  };

  const copyAccounts = () => {
    const rows = (portalResult?.created || []).map((a) => `${a.supplier}\t${a.email}\t${a.password}`).join("\n");
    navigator.clipboard.writeText(`Tedarikçi\tE-posta\tŞifre\n${rows}`);
    toast.success("Hesap listesi panoya kopyalandı");
  };

  const applyBulk = async () => {
    if (!terms.trim()) { toast.error("Ödeme koşulu girin (örn: 30 GÜN)"); return; }
    if (!onlyEmpty && !window.confirm("TÜM tedarikçilerin mevcut vadesi üzerine yazılacak. Emin misiniz?")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/suppliers/bulk-payment-terms", { payment_terms: terms.trim(), only_empty: onlyEmpty });
      toast.success(`${data.updated} tedarikçiye "${data.payment_terms}" vadesi atandı`);
      setShowBulk(false);
      setReloadTick((t) => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const bulkBtn = (
    <>
      <button
        data-testid="suppliers-portal-accounts-btn"
        onClick={createPortalAccounts}
        disabled={creatingPortal}
        className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white transition-colors disabled:opacity-50"
      >
        {creatingPortal ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Portal Hesapları Oluştur
      </button>
      <button
        data-testid="suppliers-bulk-terms-btn"
        onClick={() => setShowBulk(true)}
        className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white transition-colors"
      >
        <CalendarClock className="w-3.5 h-3.5" /> Toplu Vade Ata
      </button>
    </>
  );

  return (
    <>
      <CrudPage
        key={reloadTick}
        title="Cari Kartlar (Tedarikçiler)"
        subtitle="Vergi dairesi, IBAN ve plaka bilgileri dahil şirketinizin cari kart yönetimi."
        endpoint="/suppliers"
        testidPrefix="suppliers"
        extraActions={bulkBtn}
        fields={[
          { key: "name", label: "Firma / Cari Adı", type: "text", required: true },
          { key: "category", label: "Kategori", type: "text" },
          { key: "contact", label: "İletişim Kişisi", type: "text" },
          { key: "phone", label: "Telefon", type: "text" },
          { key: "tax_office", label: "Vergi Dairesi", type: "text" },
          { key: "tax_no", label: "Vergi No / TCKN", type: "text" },
          { key: "iban", label: "IBAN", type: "text" },
          { key: "plate", label: "Plaka", type: "text" },
          { key: "address", label: "Adres", type: "textarea" },
          { key: "payment_terms", label: "Ödeme Koşulu / Vade (örn: 30 GÜN)", type: "text" },
          { key: "notes", label: "Notlar", type: "textarea" },
        ]}
        columns={[
          { key: "name", label: "Firma" },
          { key: "category", label: "Kategori" },
          { key: "tax_office", label: "Vergi Dairesi" },
          { key: "tax_no", label: "Vergi No" },
          { key: "plate", label: "Plaka" },
          { key: "iban", label: "IBAN" },
          { key: "phone", label: "Telefon" },
          { key: "payment_terms", label: "Vade" },
        ]}
      />

      {showBulk && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBulk(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="bulk-terms-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu İşlem</div>
                <div className="font-heading font-bold text-lg">Toplu Vade Ata</div>
              </div>
              <button onClick={() => setShowBulk(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ödeme Koşulu / Vade</label>
                <input
                  data-testid="bulk-terms-input"
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  placeholder="örn: 30 GÜN"
                  className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm"
                />
                <div className="text-[11px] text-neutral-500 mt-1">Ödeme Planı, fatura tarihine bu gün sayısını ekleyerek vadeyi otomatik hesaplar.</div>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" data-testid="bulk-only-empty" checked={onlyEmpty} onChange={() => setOnlyEmpty(true)} className="accent-[#0020C2]" />
                  Sadece vadesi boş olan tedarikçiler <span className="text-[10px] uppercase tracking-widest text-[#16A34A] font-bold">önerilen</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" data-testid="bulk-all" checked={!onlyEmpty} onChange={() => setOnlyEmpty(false)} className="accent-[#DC2626]" />
                  Tüm tedarikçiler <span className="text-[10px] uppercase tracking-widest text-[#DC2626] font-bold">üzerine yazar</span>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button onClick={() => setShowBulk(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button
                  data-testid="bulk-terms-apply"
                  disabled={saving}
                  onClick={applyBulk}
                  className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-50 transition-colors"
                >
                  {saving ? "Uygulanıyor..." : "Uygula"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {portalResult && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPortalResult(null)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="portal-accounts-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Tedarikçi Portalı</div>
                <div className="font-heading font-bold text-lg">{portalResult.created_count} Hesap Oluşturuldu</div>
              </div>
              <div className="flex items-center gap-2">
                {portalResult.created_count > 0 && (
                  <button data-testid="portal-accounts-copy" onClick={copyAccounts} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white">
                    <Copy className="w-3.5 h-3.5" /> Listeyi Kopyala
                  </button>
                )}
                <button onClick={() => setPortalResult(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="p-6">
              {portalResult.created_count === 0 ? (
                <div className="text-sm text-neutral-500 text-center py-6">Tüm tedarikçilerin zaten portal hesabı var ({portalResult.existing_count} hesap).</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-[#FAFAFA]">
                    <tr>{["Tedarikçi", "E-posta", "Şifre"].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {portalResult.created.map((a) => (
                      <tr key={a.email} className="border-t border-[#F5F5F5]">
                        <td className="px-3 py-2 font-semibold">{a.supplier}</td>
                        <td className="px-3 py-2 num">{a.email}</td>
                        <td className="px-3 py-2 num">{a.password}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="mt-4 text-[11px] text-neutral-500 border-t border-[#F5F5F5] pt-3">
                Tedarikçiler bu bilgilerle giriş yaptığında yalnızca kendi cari ekstresini, hakedişlerini ve araç evraklarını görür.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
